import {useState, useCallback, useEffect, useRef} from 'react';
import {
    fetchMyTodayTimeTracking,
    fetchMyWorkTimeBalance,
    clockIn as apiClockIn,
    clockOut as apiClockOut
} from '../api/MeApi';
import {ClockInRequest, ClockOutRequest, TimeRecord, TimeRecordStatus} from '../api/TimetrackingApi';
import {WorkTimeBalance} from '../api/EmployeeApi';
import {ApiError, ErrorDetail} from '../api/types';
import {Workday} from "../api/WorkDayRecordApi";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseClockInOutReturn {
    loading: boolean;
    error: ErrorDetail | null;

    workDayRecord: Workday | null;
    workTimeBalances: WorkTimeBalance[] | null;

    /** Total worked minutes today. When clocked in, includes live elapsed time. */
    workedMinutes: number;
    workedSeconds: number;

    /** ISO string of the moment the current session started. Null when clocked out. */
    clockedInAt: string | null;

    isClockedIn: boolean;
    isClockingIn: boolean;
    isClockingOut: boolean;

    clockIn: (request?: ClockInRequest) => Promise<Workday | undefined>;
    clockOut: (request?: ClockOutRequest) => Promise<Workday | undefined>;
    refetch: () => Promise<void>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function computeWorkedMinutes(record: TimeRecord): number {
    const committed = record.actualMinutes ?? 0;

    // if (record.status === TimeRecordStatus.CLOCKED_IN && record.updatedAt) {
    //     return committed + minutesSince(record.updatedAt);
    // }

    return committed;
}

function computeWorkedSeconds(record: TimeRecord): number {
    const committed = record.actualSeconds ?? 0;

    // if (record.status === TimeRecordStatus.CLOCKED_IN && record.updatedAt) {
    //     return committed + minutesSince(record.updatedAt);
    // }

    return committed;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useClockInOut(): UseClockInOutReturn {

    const [workDayRecord, setWorkDayRecord] = useState<Workday | null>(null);
    const [workTimeBalances, setWorkTimeBalances] = useState<WorkTimeBalance[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ErrorDetail | null>(null);

    const [isClockingIn, setIsClockingIn] = useState(false);
    const [isClockingOut, setIsClockingOut] = useState(false);

    const isMountedRef = useRef(true);

    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    // Fetch

    const fetchTodayRecord = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [timeResponse, balanceResponse] = await Promise.all([
                fetchMyTodayTimeTracking(),
                fetchMyWorkTimeBalance(),
            ]);

            if (!isMountedRef.current) return;

            if (timeResponse.error) {
                setError(timeResponse.error);
                setWorkDayRecord(null);

            } else {
                setWorkDayRecord(timeResponse.data ?? null);
            }

            if (!balanceResponse.error) {
                setWorkTimeBalances(balanceResponse.data as WorkTimeBalance[]);
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            setError(err as ErrorDetail);
            console.error('Failed to fetch clock-in/out data:', err);
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTodayRecord();
    }, [fetchTodayRecord]);

    // Actions

    const refreshBalances = useCallback(async () => {
        try {
            const response = await fetchMyWorkTimeBalance();
            if (!isMountedRef.current) return;
            if (!response.error) {
                setWorkTimeBalances(response.data as WorkTimeBalance[]);
            }
        } catch (err) {
            console.error('Failed to refresh work time balances:', err);
        }
    }, []);

    const clockIn = useCallback(async (request: ClockInRequest = {}): Promise<Workday | undefined> => {
        setIsClockingIn(true);
        try {
            const response = await apiClockIn(request);

            if (response.error) {
                setError(response.error);
                setWorkDayRecord(null);

            } else {
                const record = response.data as Workday;
                setWorkDayRecord(record);
                setError(null);
                refreshBalances();
                return record;
            }
        } catch (err) {
            console.error('Clock-in error:', err);
        } finally {
            setIsClockingIn(false);
        }
    }, [refreshBalances]);

    const clockOut = useCallback(async (request: ClockOutRequest = {}): Promise<Workday | undefined> => {
        setIsClockingOut(true);
        try {
            const response = await apiClockOut(request);
            if (response.error) throw new ApiError(response.error);

            const record = response.data as Workday;
            setWorkDayRecord(record);
            refreshBalances();
            return record;
        } catch (err) {
            console.error('Clock-out error:', err);
        } finally {
            setIsClockingOut(false);
        }
    }, [refreshBalances]);

    // Derived state

    const timeRecord = workDayRecord?.timeRecord;
    const isClockedIn = timeRecord?.status === TimeRecordStatus.CLOCKED_IN;
    const workedMinutes = timeRecord ? computeWorkedMinutes(timeRecord) : 0;
    const workedSeconds = timeRecord ? computeWorkedSeconds(timeRecord) : 0;
    const clockedInAt =
        isClockedIn && timeRecord?.updatedAt
            ? new Date(timeRecord.updatedAt).toISOString()
            : null;

    return {
        loading,
        error,
        workDayRecord,
        workTimeBalances,
        workedMinutes,
        workedSeconds,
        clockedInAt,
        isClockedIn,
        isClockingIn,
        isClockingOut,
        clockIn,
        clockOut,
        refetch: fetchTodayRecord,
    };
}